import React, { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import styles from './VerticalEntryForm.module.css'
import { useStepContext } from '@/components/Context/StepContext'
import { generateUrlSafeBase64 } from '@/components/utils/securityUtils'
import { handleFormSubmission } from '@/components/utils/formSubmissionUtils'
import { SERVICE_TYPES } from '@/components/constants'
import { WorkingStatusVertical } from '../Steps/StepContent/WorkingStatusVertical'
import { ExperienceYearsVertical } from '../Steps/StepContent/ExperienceYearsVertical'
import { PrefectureVertical } from '../Steps/StepContent/PrefectureVertical'
import { FullNameVertical } from '../Steps/StepContent/FullNameVertical'
import { TellVertical } from '../Steps/StepContent/TellVertical'
import StepCounter from '../Steps/StepCounter'
import { EntryFormProps } from './types'

const TOTAL_STEPS = 5

// 縦型EF（DEV-3330のA/Bテスト結果を受けDEV-3418で正式採用）。各stepの縦型専用コンポを
// currentStep 駆動で縦積み表示する。
const VerticalEntryForm = ({ prefectures, config }: EntryFormProps) => {
    const { currentStep, formData } = useStepContext()

    const [registerToken, setRegisterToken] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isStuck, setIsStuck] = useState(false)

    useEffect(() => {
        setRegisterToken(generateUrlSafeBase64())
    }, [])

    const cards = [
        <WorkingStatusVertical gtmTags={config.stepFormLabels.step1} stepNumber={1} />,
        <ExperienceYearsVertical gtmTags={config.stepFormLabels.step2} stepNumber={2} />,
        <PrefectureVertical prefectures={prefectures} gtmTags={config.stepFormLabels.step3} stepNumber={3} />,
        <FullNameVertical gtmTags={config.stepFormLabels.step4} stepNumber={4} />,
        <TellVertical gtmTags={config.stepFormLabels.step5} stepNumber={5} isSubmitting={isSubmitting} />,
    ]

    // 新しいカードが出現（currentStepが前進）したらそのカードへ自動スクロール
    const cardRefs = useRef<Array<HTMLDivElement | null>>([])
    const prevStepRef = useRef(currentStep)
    useEffect(() => {
        if (currentStep > prevStepRef.current) {
            cardRefs.current[currentStep]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
        prevStepRef.current = currentStep
    }, [currentStep])

    // 通常配置のステッパーが画面上部から見えなくなったら固定版を表示する
    // （DEV-3480でバナーがstep1カード内へ移ったので、監視対象はステッパーのみ）
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const el = sentinelRef.current
        if (!el || typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        handleFormSubmission(
            e,
            formData,
            registerToken,
            setIsSubmitting,
            SERVICE_TYPES.FOODSLABO
        )
    }

    // DEV-3480: step1カードの内側に出すバナー。旧実装にあった上部のオレンジバナー(.banner)は
    // デザイン(Figma 2111-5990)に合わせて廃止した。instagram/googleAdがDEV-3419で入れた形と同じ。
    // 1枚のカード内にしか描画されないため、固定ヘッダー表示中にh1が2つになる旧実装の問題も解消する
    const cardBanner = (
        <header className={styles.cardBanner}>
            <h2 className={styles.cardBannerBadge}>
                簡単<span className={styles.cardBannerBadgeNumber}>30</span>
                <span className={styles.cardBannerBadgeUnit}>秒</span>！
            </h2>
            <h1 className={styles.cardBannerTitle}>無料転職サポート登録！</h1>
        </header>
    )

    return (
        <section id='entry-form' className={styles.vertical}>
            {/* 上部画像の下に通常配置（白背景・シャドウなし） */}
            <div className={styles.inFlowHead} ref={sentinelRef}>
                <div className={styles.stepperPlain}>
                    <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                </div>
            </div>

            {/* 上記が見えなくなったら白背景＋シャドウ版を上部固定 */}
            {isStuck && (
                <div className={styles.fixedHead}>
                    <div className={styles.stepperFixed}>
                        <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                {cards.map((card, i) =>
                    i <= currentStep ? (
                        <div
                            key={i}
                            ref={(el) => {
                                cardRefs.current[i] = el
                            }}
                            className={styles.card}
                            style={
                                {
                                    '--ef-follow-display':
                                        i === currentStep ? 'block' : 'none',
                                } as CSSProperties
                            }
                        >
                            {i === 0 && cardBanner}
                            {card}
                        </div>
                    ) : null
                )}
            </form>

            {isSubmitting && <div className={styles.blockingOverlay} />}
        </section>
    )
}

export default VerticalEntryForm
